import React, { useState, useEffect, ReactElement, useRef } from 'react';
import './FileUploadBox.less';
import ApplicationDetail from '../../../shared/services/applicationDetail.service';
import GrievanceUploadFiles from '../GrievanceUploadFiles';
import { useDispatch, useSelector } from 'react-redux';
import { raiseOrUpdateGrievance } from '../../../store/actions/grievanceAction';
import { centeredNotification } from '../../../utils/utility';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Col, Form, Row, Select, Tooltip } from 'antd';
import { GRIEVANCE_ACTIONS, GRIEVANCE_VIEW_ROUTE, GUTTOR_SPACE, REOPEN, TICKET_QUERY, SUCCESSFUL_TICKET_CREATION } from '../../../shared/constants/commonConstants';
import { handleRemoveFile, hasReplyStatus } from '../../crm/inPatient/service/crmUtils';
import { PaperClipOutlined } from '@ant-design/icons';
import { ReactComponent as Cross_Icon } from '../../../assets/images/svg/Grievance_Cross_Icon.svg';
import { ReactComponent as Clock_Icon } from '../../../assets/images/svg/Clock_Icon.svg';
import { GrievanceData, UploadedFile } from '../../../shared/interfaces/grievanceInterfaces';
import { UploadFile } from 'antd/lib/upload/interface';
import { RootState } from '../../../store';
import { isEmpty } from 'lodash';
import { fetchGreivanceLookUp, fetchGrievanceType } from '../../../store/actions/lookUpAction';
import { payerLookUpNamesForGrievanceType } from '../../../shared/constants/crmConstants';
import { format, parseISO } from 'date-fns';
import { GRIEVANCE_DATE_FORMAT, GRIEVANCE_TYPE_PLACEHOLDER } from '../services/grievanceConstants';
import Spinner from '../../../shared/components/Spinner';

const FileUploadBox = ({ detailConversation, currRecord, grievanceTransaction }: any): ReactElement => {
  const { lookUp: { allGrievances = [], grievanceLookup } } = useSelector((state: RootState): any => state || []);
  const [fileList, setFileList] = useState<any[]>([]);
  const [newHospitalRemark, setNewHospitalRemark] = useState<string>(''); // New Hospital Remarks
  const [isReopenClicked, setIsReopenClicked] = useState(false);
  const [isNewGrievance, setIsNewGrievance] = useState(false);
  const [showReopenBtn, setShowReopenBtn] = useState(true);
  const [selectedGrievance, setSelectedGrievance] = useState<any>(null);
  const fileListRef = useRef<UploadFile<any>[]>([]);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  const providerName = ApplicationDetail.instance.getProviderName();
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const searchParam = new URLSearchParams(search);
  const buttonType = searchParam?.get('buttonType');
  const { Option } = Select;
  const [editGrievanceForm] = Form.useForm();
  const hasAllowedStatus = currRecord?.allowStatus?.some(
    (status: any): boolean => (status.statusId === TICKET_QUERY || status.statusId === REOPEN),
  );
  const {
    payerId,
    productCode,
    hospactionId,
    isSuccess,
  } = grievanceTransaction || {};

  useEffect((): any => {
    if (detailConversation && detailConversation.length) {
      const hospitalRemarksArray: string[] = [];
      const supportRemarksArray: string[] = [];

      detailConversation?.forEach((transaction: any): any => {
        if (transaction.isRaisedByUser) {
          hospitalRemarksArray.push(transaction.remarks || '');
        } else {
          supportRemarksArray.push(transaction.remarks || '');
        }
      });
    }
  }, [detailConversation]);

  useEffect((): any => {
    if (isEmpty(grievanceLookup)) {
      dispatch(fetchGreivanceLookUp((): any => { }));
    }
  }, []);

  useEffect((): any => {
    const payerLookUpReqObj = {
      getByPayerId: true,
      getByMasterType: true,
      payerIds: [payerId],
      masterTypes: [...payerLookUpNamesForGrievanceType],
      isDicFormat: true,
      getByProductCode: productCode ? true : false,
      getByBoth: productCode ? true : false,
      productCode: productCode ? [productCode] : [],
    };
    if (hospactionId) {
      dispatch(fetchGrievanceType(payerLookUpReqObj));
    }
  }, [grievanceTransaction]);

  useEffect((): any => {
    const scrollToElement = (): void => {
      const ref = buttonType === GRIEVANCE_ACTIONS.QUERY
        ? lastMessageRef.current
        : buttonType === GRIEVANCE_ACTIONS.REOPEN
        ? footerRef.current
        : null;
      ref?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };
    const timeoutId = setTimeout(scrollToElement, 100);
    return (): void => clearTimeout(timeoutId);
  }, [buttonType, detailConversation]);

  const handleNewHospitalRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>): any => {
    setNewHospitalRemark(e.target.value);
  };

  const onSubmitNewGrievance = (data: GrievanceData, fileItem: UploadFile[]): any => {
    const {
      ticketId,
      hospactionId: hospActionId,
      payerId: payerID,
      productCode: product_Code,
      payerClaimNo,
      patientName,
      ticketStatusId,
    } = data || {};
    const uploadedFiles = fileItem
    ?.map((file: UploadFile): UploadedFile | null => ({
      fileName: file?.response?.uploadedDetails?.[0]?.fileName,
      fileGuid: file?.response?.uploadedDetails?.[0]?.fileGuid,
    }))
    .filter((fileDetails: any): boolean => fileDetails.fileName && fileDetails.fileGuid);

    const grievanceDetails = {
      ticketId,
      hospactionId: hospActionId,
      payerId: payerID,
      productCode: product_Code,
      payerClaimNo,
      patientName,
      action: ticketStatusId
        ? (hasReplyStatus(data, TICKET_QUERY) ? TICKET_QUERY : hasReplyStatus(data, REOPEN) ? REOPEN : 0)
        : 0,
      remarks: newHospitalRemark,
      attachments: uploadedFiles,
      reason: selectedGrievance,
    };
    dispatch(raiseOrUpdateGrievance(grievanceDetails, onSuccess, onError));
  };

  const onSuccess = (): any => {
    centeredNotification(SUCCESSFUL_TICKET_CREATION, 'success');
    history.push(GRIEVANCE_VIEW_ROUTE);
  };
  const onError = (errMsg: string): any => {
    centeredNotification(errMsg);
  };

  const grievanceTransactions = detailConversation || [];

  const reopen = (): void => {
    setIsReopenClicked(true);
    setShowReopenBtn(false);
  };

  const raiseNewGrievance = (): void => {
    setIsNewGrievance(true);
    setShowReopenBtn(false);
  };

  const showNewConversation = (): boolean => {
    if (buttonType === GRIEVANCE_ACTIONS.REOPEN) {
      return (isNewGrievance || isReopenClicked) && hasAllowedStatus;
    }
    return hasAllowedStatus;
  };

  const grievanceOptions = (): any[] => {
    return !isEmpty(allGrievances) ?
      allGrievances :
      grievanceLookup;
  };

  const onChangeGrievanceType = (value: string, payerMasterValue: string): void => {
    setSelectedGrievance(payerMasterValue);
  };

  const getTransactionTime = (transactionTime: string): string => {
    return format(parseISO(transactionTime), GRIEVANCE_DATE_FORMAT);
  };

  const isButtonDisabled = (): boolean => {
    if (isNewGrievance) {
      return (isEmpty(selectedGrievance) || !newHospitalRemark);
    }
    return !newHospitalRemark;
  };

  const onClickBackIcon = (): void => {
    setShowReopenBtn(true);
    setIsNewGrievance(false);
    setIsReopenClicked(false);
    editGrievanceForm.resetFields();
    setFileList([]);
    setNewHospitalRemark('');
    fileListRef.current = [];
  };

  return (
    <div className="remarks-wrapper">
      {grievanceTransactions?.map((transaction: any): any => (
        <div key={transaction.transactionId}>
          {transaction.isRaisedByUser ? (
            // Hospital Name Remarks Section (Non-editable)
            <div className="remark-container left">
              <div className="remark-bubble">
                <label className="grievance-label">{providerName} {transaction.reason && `<${transaction.reason}>`}</label>
                <div className="remark-box">
                  <textarea className="input-box" value={transaction.remarks} readOnly={true} />
                  <div className="raised-grievance-upload-icon-container">
                    <GrievanceUploadFiles key={`upload-${transaction.transactionId}`} setFileList={setFileList}
                      fileList={transaction?.documents} grievanceTransactions={transaction?.documents || []} showUploadIcon={false}
                      fileListRef={fileListRef} />
                  </div>
                </div>
                <span className="grievance-createdDate"><Clock_Icon />{getTransactionTime(transaction?.createdDate)}</span>
              </div>
            </div>
          ) : (transaction?.remarks &&
            // LPA Support Remarks Section (Non-editable)
            <div className="remark-container right">
              <div className="remark-bubble">
                <label className="grievance-label">IHX Support</label>
                <div className="remark-box">
                  <textarea className="input-box" value={transaction.remarks} readOnly={true} />
                  <div className="raised-grievance-upload-icon-container">
                    <GrievanceUploadFiles key={`upload-${transaction.transactionId}`} setFileList={setFileList}
                      fileList={fileList} grievanceTransactions={transaction?.documents} showUploadIcon={false} fileListRef={fileListRef} />
                  </div>
                </div>
                <span className="grievance-createdDate"><Clock_Icon />{getTransactionTime(transaction?.createdDate)}</span>
              </div>
            </div>
          )}
        </div>
      ))}

      {showNewConversation() && (
        <div className="new-remark-container left">
          <div className="remark-bubble-1">
            <label className="grievance-grievance-label">{providerName}</label>
            {isNewGrievance && <Form
              form={editGrievanceForm}
              layout="vertical"
              autoComplete={'off'}
            >
              <Row className="mt-sm" gutter={GUTTOR_SPACE}>
                <Col md={12}>
                  <Form.Item name="grievanceType" label={<div className="font-input">Grievance Type</div>}>
                    <Select allowClear={true} placeholder={GRIEVANCE_TYPE_PLACEHOLDER} onChange={(value: any): any => {
                      const selectedGrievanceReason = grievanceOptions()?.find(
                        (grievance: any): any => (grievance?.payerMasterId || grievance?.id) === value,
                      );
                      onChangeGrievanceType(value, (selectedGrievanceReason?.payerMasterValue || selectedGrievanceReason?.value));
                    }}>
                      {grievanceOptions()?.map((grievance: any): any => (
                        <Option key={grievance?.payerMasterId || grievance?.id} value={grievance?.payerMasterId || grievance?.id}>
                          {grievance?.payerMasterValue || grievance?.value}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form>}
            <div className="remark-box">
              <textarea
                className="input-box"
                placeholder="Enter your remarks here..."
                value={newHospitalRemark}
                onChange={handleNewHospitalRemarksChange}
              />

              <div className="upload-section">
                <div className="upload-icon-container">
                  <GrievanceUploadFiles key="upload" setFileList={setFileList}
                    fileList={fileList} showUploadIcon={true} fileListRef={fileListRef} />
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
      <div className="uploaded-files grievance-uploaded-file-items">

        {(fileList?.length > 0) &&
          fileList.map((file: any): any => (
            <Col key={file?.uid} md={24} className="uploaded-file">
              <PaperClipOutlined />&nbsp;{file?.name}
              <Tooltip title="Remove">
                <Cross_Icon
                  className="grievance-remove-file-icon"
                  onClick={(): any => handleRemoveFile(file, setFileList, fileListRef)}
                />
              </Tooltip>
            </Col>
          ))}
      </div>

      {isSuccess ? (buttonType === GRIEVANCE_ACTIONS.REOPEN && showReopenBtn) ?
        <Row className="footer" gutter={GUTTOR_SPACE} ref={footerRef}>
          <Col><Button className={!newHospitalRemark ? 'disabled-submit-btn' : 'submit-button'}
            onClick={(): any => reopen()}>
            {GRIEVANCE_ACTIONS.REOPEN}
          </Button></Col>
          <Col><Button className={!newHospitalRemark ? 'disabled-submit-btn' : 'submit-button'}
            onClick={(): any => raiseNewGrievance()}>
            {GRIEVANCE_ACTIONS.NEW_GRIEVANCE}
          </Button>
          </Col>
        </Row> :
        showNewConversation() &&
        <Row className="footer" gutter={GUTTOR_SPACE} ref={lastMessageRef}>
          <Col><Button className={isButtonDisabled() ? 'disabled-submit-btn' : 'submit-button'}
            onClick={(): any => onSubmitNewGrievance(currRecord, fileList)} disabled={isButtonDisabled()}>
            {GRIEVANCE_ACTIONS.SUBMIT}
          </Button>
          </Col>
          {buttonType === GRIEVANCE_ACTIONS.REOPEN && <Col><Button className={'submit-button'}
            onClick={(): any => onClickBackIcon()}>
            {GRIEVANCE_ACTIONS.BACK}
          </Button></Col>}
        </Row> : <Spinner />
      }
    </div>
  );
};

export default FileUploadBox;
